import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { CalendarDays, Layers3, Pencil, Search, ShieldCheck, ShieldX, X } from 'lucide-react';
import ExportButton from '../../components/common/ExportButton';
import FilterSelect from '../../components/common/FilterSelect';
import { inventoryAPI, productsAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useStoreContextStore } from '../../store/storeContextStore';
import { downloadExcelWorkbook } from '../../utils/exportExcel';

const formatLocalDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const shiftLocalDateKey = (dateKey, days) => {
  const [year, month, day] = String(dateKey || '').split('-').map(Number);
  const date = new Date(year, (month || 1) - 1, day || 1);
  date.setDate(date.getDate() + days);
  return formatLocalDateKey(date);
};

const COUNT_STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'counted', label: 'Counted' },
  { value: 'uncounted', label: 'Uncounted' },
];

const DIFFERENCE_STATUS_OPTIONS = [
  { value: 'all', label: 'All Difference' },
  { value: 'matched', label: 'Matched' },
  { value: 'unmatched', label: 'Unmatched' },
  { value: 'na', label: 'N/A' },
];

const STOCK_STATE_OPTIONS = [
  { value: 'all', label: 'All Stock' },
  { value: 'out', label: 'Stock Out (0 qty)' },
  { value: 'available', label: 'Stock Available (> 0 qty)' },
];

const FullInventoryReport = () => {
  const queryClient = useQueryClient();
  const { hasFeatureAccess, isAdmin } = useAuthStore();
  const selectedStore = useStoreContextStore((state) => state.selectedStore);
  const canViewPage = hasFeatureAccess('inventory_count.report');
  const canManageSchedule = isAdmin() || hasFeatureAccess('inventory_count.manage');
  const selectedStoreId = selectedStore?.storeInfoId || selectedStore?.store_info_id || selectedStore?.legacyStoreId || selectedStore?.legacy_store_id || '';
  const todayKey = formatLocalDateKey();
  const defaultFromDate = shiftLocalDateKey(todayKey, -7);

  const [filter, setFilter] = useState({
    fromDate: defaultFromDate,
    toDate: todayKey,
    search: '',
    countStatus: 'all',
    differenceStatus: 'all',
    systemStockStatus: 'all',
    physicalStockStatus: 'all',
  });
  const [scheduleDate, setScheduleDate] = useState(todayKey);
  const [scheduleSellerId, setScheduleSellerId] = useState('');
  const [scheduleNote, setScheduleNote] = useState('');
  const [editingScheduleId, setEditingScheduleId] = useState('');

  const { data, isLoading } = useQuery(
    ['fullInventoryReport', selectedStoreId, filter],
    () => inventoryAPI.getFullCountReport({
      dateFrom: filter.fromDate,
      dateTo: filter.toDate,
      search: filter.search,
      countStatus: filter.countStatus,
      differenceStatus: filter.differenceStatus,
      systemStockStatus: filter.systemStockStatus,
      physicalStockStatus: filter.physicalStockStatus,
      ...(selectedStoreId ? { storeInfoId: selectedStoreId } : {}),
    }),
    {
      enabled: canViewPage && Boolean(selectedStoreId),
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
    }
  );

  const { data: schedulesData } = useQuery(
    ['fullInventoryScheduleList', filter.fromDate, filter.toDate, selectedStoreId],
    () => inventoryAPI.getFullCountSchedules({
      dateFrom: filter.fromDate,
      dateTo: filter.toDate,
      ...(selectedStoreId ? { storeInfoId: selectedStoreId } : {}),
    }),
    {
      enabled: canViewPage && Boolean(selectedStoreId),
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
    }
  );

  const { data: sellersData } = useQuery(
    ['fullInventorySellerOptions', selectedStoreId],
    () => productsAPI.getSellers({ storeInfoId: selectedStoreId, includeIds: true }),
    {
      enabled: canManageSchedule && Boolean(selectedStoreId),
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
    }
  );

  const rows = useMemo(() => data?.data?.data || [], [data]);
  const meta = data?.data?.meta || {};
  const schedules = schedulesData?.data?.data || [];
  const sellerOptions = (sellersData?.data?.data || []).filter((seller) => seller?.id);

  const scheduleMutation = useMutation(
    (payload) => inventoryAPI.saveFullCountSchedule(payload),
    {
      onSuccess: async (_response, variables) => {
        toast.success(variables?.id ? 'Full inventory count day updated.' : 'Full inventory count day scheduled.');
        setEditingScheduleId('');
        setScheduleDate(todayKey);
        setScheduleSellerId('');
        setScheduleNote('');
        await queryClient.invalidateQueries('fullInventoryScheduleList');
        await queryClient.invalidateQueries('fullInventorySchedulesForTask');
        await queryClient.invalidateQueries('fullInventoryReport');
        await queryClient.invalidateQueries('upcomingTasks');
        await queryClient.invalidateQueries('notifications');
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to schedule full inventory count');
      },
    }
  );

  const summary = useMemo(() => ({
    totalRows: rows.length,
    counted: rows.filter((row) => row.counted).length,
    uncounted: rows.filter((row) => !row.counted).length,
    totalQuantity: rows.reduce((sum, row) => sum + Number(row.totalQuantity || 0), 0),
  }), [rows]);

  const exportRows = () => {
    if (!rows.length) return;

    downloadExcelWorkbook({
      fileName: `full-inventory-report-${filter.fromDate}-to-${filter.toDate}.xlsx`,
      sheetName: 'Full Inventory',
      columns: [
        { header: 'Date', key: 'date', width: 14 },
        { header: 'Store', key: 'storeName', width: 20 },
        { header: 'Seller', key: 'sellerName', width: 22 },
        { header: 'SKU', key: 'sku', width: 18 },
        { header: 'Barcode', key: 'barcode', width: 18 },
        { header: 'Product', key: 'productName', width: 34 },
        { header: 'Shop Count', key: 'shopCount', width: 12 },
        { header: 'Warehouse Count', key: 'warehouseCount', width: 16 },
        { header: 'Total Qty', key: 'totalQuantity', width: 12 },
        { header: 'System Qty', width: 12, value: (row) => Number(row.systemStockQty || 0) },
        { header: 'Difference', width: 12, value: (row) => (row.counted ? Number(row.difference || 0) : 'N/A') },
        { header: 'Category', key: 'categoryName', width: 18 },
        { header: 'Parent Category', key: 'parentCategoryName', width: 22 },
        { header: 'Count Status', width: 14, value: (row) => (row.counted ? 'Counted' : 'Uncounted') },
        { header: 'Difference Status', width: 16, value: (row) => (row.counted ? (Number(row.difference || 0) === 0 ? 'Matched' : 'Unmatched') : 'N/A') },
        { header: 'Updated By', width: 18, value: (row) => row.updatedBy || '-' },
        { header: 'Updated At', width: 22, value: (row) => (row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '-') },
      ],
      rows,
    });
  };

  const handleScheduleSave = () => {
    if (!selectedStoreId || !scheduleDate) {
      toast.error('Select a store from the header and date first.');
      return;
    }

    scheduleMutation.mutate({
      ...(editingScheduleId ? { id: editingScheduleId } : {}),
      storeInfoId: selectedStoreId,
      scheduledDate: scheduleDate,
      sellerId: scheduleSellerId || null,
      note: scheduleNote.trim(),
    });
  };

  const resetScheduleForm = () => {
    setEditingScheduleId('');
    setScheduleDate(todayKey);
    setScheduleSellerId('');
    setScheduleNote('');
  };

  const editSchedule = (schedule) => {
    if (!schedule?.canEdit) {
      toast.error('This scheduled count day already has entries and cannot be edited.');
      return;
    }

    setEditingScheduleId(String(schedule.id || ''));
    setScheduleDate(schedule.scheduled_date || todayKey);
    setScheduleSellerId(String(schedule.seller_id || schedule.sellerId || ''));
    setScheduleNote(schedule.note || '');
  };

  if (!canViewPage) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Full Inventory Report</h1>
        <div className="card">
          <p className="text-gray-500">Access denied.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-[24px] font-bold tracking-tight text-slate-900">Full Inventory Report</h1>
          <p className="text-sm text-slate-600">Review counted and uncounted products for scheduled full counts.</p>
        </div>
        <ExportButton onClick={exportRows} disabled={!rows.length} />
      </div>

      <div className="grid gap-3 lg:grid-cols-4">
        <div className="card border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center gap-3">
            <Layers3 className="h-5 w-5 text-slate-600" />
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-700">Scheduled Rows</p>
              <p className="text-xl font-bold text-slate-900">{summary.totalRows}</p>
            </div>
          </div>
        </div>
        <div className="card border border-emerald-200 bg-emerald-50 p-4">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-emerald-800">Counted</p>
              <p className="text-xl font-bold text-emerald-700">{summary.counted}</p>
            </div>
          </div>
        </div>
        <div className="card border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center gap-3">
            <ShieldX className="h-5 w-5 text-amber-600" />
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-amber-800">Uncounted</p>
              <p className="text-xl font-bold text-amber-700">{summary.uncounted}</p>
            </div>
          </div>
        </div>
        <div className="card border border-primary-200 bg-primary-50 p-4">
          <div className="flex items-center gap-3">
            <CalendarDays className="h-5 w-5 text-primary-600" />
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-primary-800">Total Quantity</p>
              <p className="text-xl font-bold text-primary-700">{summary.totalQuantity}</p>
            </div>
          </div>
        </div>
      </div>

      {canManageSchedule ? (
        <div className="card border border-primary-200 bg-primary-50/70 p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-primary-900">
                {editingScheduleId ? 'Edit Count Day' : 'Set Count Day'}
              </h2>
              <p className="text-xs text-primary-700">
                {editingScheduleId ? 'Saved schedules can be edited until count entries are recorded.' : 'Uses the currently selected header store.'}
              </p>
            </div>
            {editingScheduleId ? (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-lg border border-primary-200 bg-white px-3 py-1.5 text-xs font-semibold text-primary-700 hover:bg-primary-50"
                onClick={resetScheduleForm}
                disabled={scheduleMutation.isLoading}
              >
                <X className="h-3.5 w-3.5" />
                Cancel Edit
              </button>
            ) : null}
          </div>
          <div className="grid gap-3 lg:grid-cols-[180px_minmax(220px,1fr)_minmax(240px,1.5fr)_auto] lg:items-end">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Schedule Date</label>
              <input
                type="date"
                value={scheduleDate}
                min={editingScheduleId ? undefined : todayKey}
                onChange={(event) => setScheduleDate(event.target.value)}
                className="input"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Seller Scope</label>
              <select
                value={scheduleSellerId}
                onChange={(event) => setScheduleSellerId(event.target.value)}
                className="input"
              >
                <option value="">All Sellers</option>
                {sellerOptions.map((seller) => (
                  <option key={seller.id} value={seller.id}>
                    {seller.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Note</label>
              <input
                type="text"
                value={scheduleNote}
                onChange={(event) => setScheduleNote(event.target.value)}
                className="input"
                placeholder="Optional reminder for the store team"
              />
            </div>
            <button
              type="button"
              onClick={handleScheduleSave}
              disabled={scheduleMutation.isLoading}
              className="btn-primary"
            >
              {scheduleMutation.isLoading ? 'Saving...' : editingScheduleId ? 'Update Count Day' : 'Set Count Day'}
            </button>
          </div>
        </div>
      ) : null}

      <div className="card p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[160px]">
            <label className="mb-1 block text-sm font-medium text-gray-700">Date From</label>
            <input
              type="date"
              value={filter.fromDate}
              max={filter.toDate}
              onChange={(event) => setFilter((current) => ({ ...current, fromDate: event.target.value }))}
              className="input"
            />
          </div>
          <div className="min-w-[160px]">
            <label className="mb-1 block text-sm font-medium text-gray-700">Date To</label>
            <input
              type="date"
              value={filter.toDate}
              min={filter.fromDate}
              onChange={(event) => setFilter((current) => ({ ...current, toDate: event.target.value }))}
              className="input"
            />
          </div>
          <FilterSelect
            label="Count Status"
            value={filter.countStatus}
            options={COUNT_STATUS_OPTIONS}
            onChange={(nextValue) => setFilter((current) => ({ ...current, countStatus: nextValue }))}
            className="min-w-[170px]"
          />
          <FilterSelect
            label="Difference"
            value={filter.differenceStatus}
            options={DIFFERENCE_STATUS_OPTIONS}
            onChange={(nextValue) => setFilter((current) => ({ ...current, differenceStatus: nextValue }))}
            className="min-w-[170px]"
          />
          <FilterSelect
            label="System Stock"
            value={filter.systemStockStatus}
            options={STOCK_STATE_OPTIONS}
            onChange={(nextValue) => setFilter((current) => ({ ...current, systemStockStatus: nextValue }))}
            className="min-w-[190px]"
          />
          <FilterSelect
            label="Physical Stock"
            value={filter.physicalStockStatus}
            options={STOCK_STATE_OPTIONS}
            onChange={(nextValue) => setFilter((current) => ({ ...current, physicalStockStatus: nextValue }))}
            className="min-w-[190px]"
          />
          <div className="min-w-[260px] flex-[1.1_1_320px]">
            <label className="mb-1 block text-sm font-medium text-gray-700">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400" />
              <input
                type="text"
                value={filter.search}
                onChange={(event) => setFilter((current) => ({ ...current, search: event.target.value }))}
                placeholder="Search date, product, SKU, barcode, seller..."
                className="input pl-10"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden p-0">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b px-5 py-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Full Inventory Count Results</h2>
            <p className="text-sm text-gray-500">
              Showing scheduled full inventory rows from {meta.fromDate || filter.fromDate} to {meta.toDate || filter.toDate}
            </p>
          </div>
        </div>

        <div className="table-scroll-shell">
          <table className="min-w-[1860px] divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Date</th>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Store</th>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Seller</th>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">SKU</th>
                <th className="sticky top-0 w-[20ch] min-w-[20ch] max-w-[20ch] bg-gray-50 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Barcode</th>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Product Name</th>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Shop Count</th>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Warehouse Count</th>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Total Qty</th>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">System Qty</th>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Difference</th>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Category</th>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Parent Category</th>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">Difference Status</th>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {isLoading ? (
                <tr>
                  <td colSpan="16" className="px-6 py-8 text-center text-gray-500">Loading full inventory report...</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="16" className="px-6 py-8 text-center text-gray-500">No full inventory rows found for the selected filters.</td>
                </tr>
              ) : rows.map((row) => (
                <tr key={`${row.date}-${row.id}`} className={row.counted ? 'bg-emerald-50/20 hover:bg-emerald-50/40' : 'bg-amber-50/20 hover:bg-amber-50/40'}>
                  <td className="px-4 py-3 text-sm text-gray-700">{row.date}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{row.storeName}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{row.sellerName}</td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-600">{row.sku}</td>
                  <td className="w-[20ch] min-w-[20ch] max-w-[20ch] px-4 py-3 text-sm font-mono text-gray-600">
                    <div className="barcode-scroll-cell" title={row.barcode || '-'}>
                      {row.barcode || '-'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.productName}</td>
                  <td className="px-4 py-3 text-right text-sm text-gray-700">{row.shopCount}</td>
                  <td className="px-4 py-3 text-right text-sm text-gray-700">{row.warehouseCount}</td>
                  <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">{row.totalQuantity}</td>
                  <td className="px-4 py-3 text-right text-sm text-gray-700">{Number(row.systemStockQty || 0)}</td>
                  <td className={`px-4 py-3 text-right text-sm font-semibold ${!row.counted ? 'text-slate-500' : Number(row.difference || 0) === 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {row.counted ? Number(row.difference || 0) : 'N/A'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{row.categoryName}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{row.parentCategoryName}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${row.counted ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {row.counted ? 'Counted' : 'Uncounted'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                      !row.counted
                        ? 'bg-slate-100 text-slate-600'
                        : Number(row.difference || 0) === 0
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-rose-100 text-rose-700'
                    }`}>
                      {row.counted ? (Number(row.difference || 0) === 0 ? 'Matched' : 'Unmatched') : 'N/A'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    <div>{row.updatedBy || '-'}</div>
                    <div className="mt-0.5 text-xs">{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '-'}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {schedules.length > 0 ? (
        <div className="card p-4">
          <h2 className="text-base font-semibold text-gray-900">Scheduled Count Days</h2>
          <div className="mt-3 flex flex-wrap gap-3">
            {schedules.map((schedule) => (
              <div key={schedule.id} className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-slate-900">{schedule.storeName}</div>
                    <div>{schedule.scheduled_date}</div>
                  </div>
                  {canManageSchedule && schedule.canEdit ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                      onClick={() => editSchedule(schedule)}
                      disabled={scheduleMutation.isLoading}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </button>
                  ) : (
                    <span className="rounded-full bg-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                      Executed
                    </span>
                  )}
                </div>
                <div className="mt-1 text-xs font-semibold text-primary-700">
                  {schedule.sellerScopeName || schedule.seller_scope_name || schedule.sellerName || schedule.seller_name || 'All Sellers'}
                </div>
                {schedule.isExecuted && schedule.lastExecutedAt ? (
                  <div className="mt-1 text-xs text-slate-500">
                    Last entry: {new Date(schedule.lastExecutedAt).toLocaleString()}
                  </div>
                ) : null}
                {schedule.note ? <div className="mt-1 text-xs text-slate-500">{schedule.note}</div> : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

    </div>
  );
};

export default FullInventoryReport;
